import React, { useState, useEffect } from 'react';
import { 
  Truck, Package, ArrowRight, Shield, Clock, DollarSign, 
  CheckCircle, Users, Zap, Target, TrendingUp, Globe,
  ChevronRight, Star, BadgeCheck, Quote
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import FloatingChatWidget from '@/components/FloatingChatWidget';

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/0ee8c733-9ebe-4e41-9af6-e5aa20e6c9c1/images/664a6582c52f759cf8aa9c469d5205aa85a496bab31400c484f1532a982ea379.png";

// Animated counter hook
const useAnimatedCounter = (end, duration = 2000, start = 0) => {
  const [count, setCount] = useState(start);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    if (!hasStarted) return;
    
    let startTimestamp = null;
    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      setCount(Math.floor(progress * (end - start) + start));
      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };
    window.requestAnimationFrame(step);
  }, [hasStarted, end, duration, start]);

  return [count, setHasStarted];
};

// Stats Counter Component — capability stats (no fabricated user counts)
const AnimatedStatsCounter = () => {
  const stats = [
    { icon: Clock,     value: '24/7',   label: 'Never sleeps, never takes vacation',     color: 'text-blue-500' },
    { icon: Zap,       value: '30 min', label: 'Backup carrier dispatched in 30 minutes', color: 'text-amber-500' },
    { icon: DollarSign,value: '6%',     label: 'Less than any human dispatcher',          color: 'text-emerald-500' },
    { icon: BadgeCheck,value: '100%',   label: 'Audit trail on every load action',        color: 'text-purple-500' },
  ];

  return (
    <div id="stats-counter" className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-800 rounded-lg overflow-hidden">
      {stats.map((s, i) => {
        const Icon = s.icon;
        return (
          <div key={i} className="bg-zinc-900 p-6 md:p-8 text-center" data-testid={`capability-stat-${i}`}>
            <Icon className={`w-6 h-6 ${s.color} mx-auto mb-3`} />
            <p className="text-3xl md:text-4xl font-black text-white font-mono tracking-tight">{s.value}</p>
            <p className="text-zinc-500 text-sm mt-1">{s.label}</p>
          </div>
        );
      })}
    </div>
  );
};

// Testimonials data
const testimonials = [
  {
    quote: "I fired my dispatcher and signed Tempus the same week. Same booked loads, way better rate confirmations, half the headaches.",
    name: "Mike Richardson",
    role: "Owner-Operator, Texas",
    rating: 5,
    equipment: "Dry Van"
  },
  {
    quote: "Posted a load on Sunday night. By 7am Monday Tempus had three signed rate confirmations waiting for me. Didn't lift a finger.",
    name: "Sarah Chen",
    role: "Logistics Manager, Atlanta",
    rating: 5,
    equipment: "Broker"
  },
  {
    quote: "Just 6% on what I actually book. No monthly fee when I'm not moving — beats every other dispatch service I've used.",
    name: "James Walker",
    role: "Owner-Operator, Ohio",
    rating: 5,
    equipment: "Flatbed"
  },
  {
    quote: "Pay-when-you-earn 6% beats every platform that charges me even when I'm parked. Tempus only earns when I earn.",
    name: "Carlos Mendez",
    role: "Small Fleet Owner, Florida",
    rating: 5,
    equipment: "Reefer"
  }
];

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-zinc-950 text-white" data-testid="landing-page">
      {/* Subtle grid background */}
      <div className="fixed inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0wIDBoNDB2NDBIMHoiLz48cGF0aCBkPSJNNDAgMEgwdjQwaDQwVjB6TTEgMWgzOHYzOEgxVjF6IiBmaWxsPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMDMpIi8+PC9nPjwvc3ZnPg==')] opacity-50 pointer-events-none"></div>
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-zinc-950/90 backdrop-blur-md border-b border-zinc-800" data-testid="navbar">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-20">
            <a href="/">
              <img 
                src={LOGO_URL} 
                alt="Tempus Dispatch" 
                className="h-36 md:h-40"
              />
            </a>
            <div className="hidden md:flex items-center gap-8">
              <a href="#how-it-works" className="text-zinc-400 hover:text-white font-medium transition-colors">How It Works</a>
              <a href="#for-shippers" className="text-zinc-400 hover:text-white font-medium transition-colors">For Shippers</a>
              <a href="#for-carriers" className="text-zinc-400 hover:text-white font-medium transition-colors">For Carriers</a>
              <a href="/pricing" className="text-zinc-400 hover:text-white font-medium transition-colors" data-testid="nav-pricing-link">Pricing</a>
            </div>
            <div className="flex items-center gap-3">
              <a href="/loads" className="hidden sm:block text-zinc-300 hover:text-white font-medium transition-colors">
                Find Loads
              </a>
              <a href="/post-load" className="bg-blue-600 hover:bg-blue-500 text-white px-5 py-2.5 rounded-md font-bold transition-colors shadow-[0_0_20px_rgba(59,130,246,0.3)]" data-testid="nav-post-load-btn">
                Post a Load
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Split Screen */}
      <section className="pt-28 pb-20 relative overflow-hidden" data-testid="hero-section">
        {/* Glow effect */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-500/10 rounded-full blur-3xl pointer-events-none"></div>
        
        <div className="max-w-7xl mx-auto px-4 md:px-8 relative">
          {/* Tagline */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-zinc-900 border border-zinc-800 text-zinc-300 px-4 py-2 rounded-md text-sm font-medium mb-6">
              <Zap className="w-4 h-4 text-amber-500" />
              AI Dispatch Agent · for owner-operators &amp; small fleets
            </div>
            <h1 className="text-5xl md:text-7xl font-black tracking-tighter mb-6">
              Stop calling dispatchers.<br/>
              <span className="text-blue-500">Drive the truck.</span>
            </h1>
            <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
              Tempus is your AI dispatcher. It books your loads, sends rate confirmations, and auto-dispatches backup coverage —{' '}
              <span className="text-white font-medium">for 6% of your gross. Less than any human dispatcher. Pay only when you earn.</span>
            </p>
          </div>

          {/* Two-sided CTA */}
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Shipper Card */}
            <a href="/post-load" className="group" data-testid="shipper-cta">
              <div className="bg-zinc-900/50 border border-zinc-800 hover:border-blue-500/50 rounded-lg p-8 transition-all duration-300 h-full">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <Package className="w-7 h-7 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-zinc-500 text-sm font-medium uppercase tracking-wider">I Ship Freight</p>
                    <h3 className="text-2xl font-bold text-white">Find Carriers</h3>
                  </div>
                </div>
                <p className="text-zinc-400 mb-6">Post your load. Tempus matches you with vetted carriers and generates a compliant rate confirmation. No per-load fees.</p>
                <div className="flex items-center gap-2 text-blue-500 font-bold group-hover:gap-4 transition-all">
                  Post a Load <ArrowRight className="w-5 h-5" />
                </div>
              </div>
            </a>

            {/* Carrier Card — HERO PRODUCT */}
            <a href="/driver-signup" className="group" data-testid="carrier-cta">
              <div className="bg-gradient-to-br from-amber-500/10 to-blue-500/10 border-2 border-amber-500/40 hover:border-amber-500/70 rounded-lg p-8 transition-all duration-300 h-full relative">
                <div className="absolute -top-3 left-6 bg-amber-500 text-zinc-950 text-xs font-bold px-3 py-1 rounded-full">
                  Hero product
                </div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-amber-500/20 rounded-lg flex items-center justify-center">
                    <Truck className="w-7 h-7 text-amber-500" />
                  </div>
                  <div>
                    <p className="text-zinc-500 text-sm font-medium uppercase tracking-wider">I Drive Trucks</p>
                    <h3 className="text-2xl font-bold text-white">Get an AI Dispatcher</h3>
                  </div>
                </div>
                <p className="text-zinc-400 mb-6">Sign one agreement. Tempus books your loads, sends rate confirmations, and chases backup coverage 24/7 — for <strong className="text-amber-400">6% of your gross</strong>. No monthly fee.</p>
                <div className="flex items-center gap-2 text-amber-500 font-bold group-hover:gap-4 transition-all">
                  Hire Tempus as Your Dispatcher <ArrowRight className="w-5 h-5" />
                </div>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Stats Section - Animated Counter */}
      <section className="py-16 border-y border-zinc-800" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <AnimatedStatsCounter />
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black tracking-tighter mb-4">How It Works</h2>
            <p className="text-xl text-zinc-400">Three steps. That&apos;s it.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { 
                step: '01', 
                title: 'Post or Browse', 
                desc: 'Shippers post loads with details. Carriers browse the load board.',
                icon: Target
              },
              { 
                step: '02', 
                title: 'Connect & Bid', 
                desc: 'Carriers bid on loads. Shippers choose the best option.',
                icon: Users
              },
              { 
                step: '03', 
                title: 'Move & Get Paid', 
                desc: 'Carrier delivers. Broker pays carrier directly. Tempus charges no per-load fee.',
                icon: TrendingUp
              },
            ].map((item, idx) => (
              <div key={idx} className="relative">
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-8 h-full">
                  <div className="text-6xl font-black text-zinc-800 absolute top-4 right-6">{item.step}</div>
                  <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mb-6">
                    <item.icon className="w-6 h-6 text-blue-500" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                  <p className="text-zinc-400">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For Shippers */}
      <section id="for-shippers" className="py-20 bg-zinc-900/50 border-y border-zinc-800" data-testid="shippers-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-blue-500/20 text-blue-400 px-3 py-1 rounded-md text-sm font-medium mb-6">
                <Package className="w-4 h-4" /> For Shippers & Brokers
              </div>
              <h2 className="text-4xl md:text-5xl font-black tracking-tighter mb-6">
                Post Once.<br/>Get Bids in Minutes.
              </h2>
              <p className="text-xl text-zinc-400 mb-8">
                Stop chasing carriers. Post your load and let verified carriers come to you with competitive bids.
              </p>
              <div className="space-y-4 mb-8">
                {[
                  'Search 733,000+ FMCSA-verified carriers (national registry)',
                  'Get multiple bids within minutes',
                  'Review carrier ratings and verification badges',
                  'Auto-generated rate cons & invoices (templates only)',
                  'Pay carriers directly — no broker, no escrow, no per-load fee'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <span className="text-zinc-300">{item}</span>
                  </div>
                ))}
              </div>
              <a href="/post-load" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-8 py-4 rounded-md font-bold transition-colors shadow-[0_0_20px_rgba(59,130,246,0.3)]" data-testid="post-load-cta">
                Post Your First Load <ArrowRight className="w-5 h-5" />
              </a>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-8">
              <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                <Star className="w-5 h-5 text-amber-500" /> Why Shippers Choose Us
              </h3>
              <div className="space-y-6">
                {[
                  { title: 'Flat Monthly Pricing', desc: '$2,500/mo Business or custom Enterprise. Software fees only — Tempus takes zero percent of your freight.' },
                  { title: 'Verified Carriers Only', desc: 'Every carrier is vetted with MC, DOT, and insurance verification.' },
                  { title: 'Real-Time Tracking', desc: 'Know where your freight is at all times.' },
                  { title: 'Document Management', desc: 'Rate cons, BOLs, and PODs all in one place.' },
                ].map((item, idx) => (
                  <div key={idx} className="border-b border-zinc-800 pb-4 last:border-0 last:pb-0">
                    <h4 className="font-bold text-white mb-1">{item.title}</h4>
                    <p className="text-sm text-zinc-500">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Carriers */}
      <section id="for-carriers" className="py-20" data-testid="carriers-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1 bg-zinc-900 border border-zinc-800 rounded-lg p-8">
              <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                <BadgeCheck className="w-5 h-5 text-green-500" /> What Tempus Does For You
              </h3>
              <div className="space-y-6">
                {[
                  { title: 'Books Your Loads', desc: 'Autopilot scans every load board, bids on your behalf, and signs the rate confirmation as your authorized agent — while you drive.' },
                  { title: 'Handles Backup Coverage', desc: 'Breakdown on Friday afternoon? One tap. Tempus dispatches a backup carrier in 30 minutes and notifies the broker. You stay in good standing.' },
                  { title: '24/7 — Never Sleeps', desc: 'No "the dispatcher will get back to you on Monday." Tempus negotiates rates, sends paperwork, and answers brokers around the clock.' },
                  { title: 'Pay Only When You Earn', desc: '6% of gross monthly revenue. Less than any human dispatcher (industry average: 7%). No monthly fee, no load limits, no setup cost.' },
                ].map((item, idx) => (
                  <div key={idx} className="border-b border-zinc-800 pb-4 last:border-0 last:pb-0">
                    <h4 className="font-bold text-white mb-1">{item.title}</h4>
                    <p className="text-sm text-zinc-500">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-400 px-3 py-1 rounded-md text-sm font-medium mb-6">
                <Truck className="w-4 h-4" /> For Owner-Operators &amp; Small Fleets
              </div>
              <h2 className="text-4xl md:text-5xl font-black tracking-tighter mb-6">
                Your AI Dispatcher.<br/>Working While You Drive.
              </h2>
              <p className="text-xl text-zinc-400 mb-8">
                Sign one Carrier Dispatch Agreement. Tempus becomes your authorized agent —
                booking loads, sending rate cons, chasing backup coverage. You drive. We dispatch.
              </p>
              <div className="space-y-4 mb-8">
                {[
                  'Replaces your 7% human dispatcher at 6%',
                  'Autopilot books loads on your behalf',
                  'Backup carrier auto-dispatch in 30 min',
                  'Full audit trail — every action timestamped',
                  'Cancel anytime. Month-to-month. No contract lock-in.'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                    <span className="text-zinc-300">{item}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-4">
                <a href="/driver-signup" className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black px-8 py-4 rounded-md font-bold transition-colors" data-testid="browse-loads-cta">
                  Hire Tempus as Your Dispatcher <ArrowRight className="w-5 h-5" />
                </a>
                <a href="/driver-signup" className="inline-flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-white px-8 py-4 rounded-md font-bold transition-colors border border-zinc-700">
                  Join as Carrier
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 bg-zinc-900/50 border-y border-zinc-800" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-black tracking-tighter mb-4">Built on Trust</h2>
          <p className="text-xl text-zinc-400 mb-12 max-w-2xl mx-auto">
            Every carrier is verified. Every transaction is tracked. Every party is protected.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: 'MC & DOT Verified', desc: 'All carriers verified through FMCSA' },
              { icon: BadgeCheck, title: 'Insurance Checked', desc: 'Cargo and liability coverage confirmed' },
              { icon: Star, title: 'Rating System', desc: 'Transparent reviews from both sides' },
              { icon: Clock, title: '24/7 Support', desc: 'Help when you need it' },
            ].map((item, idx) => (
              <div key={idx} className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
                <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-6 h-6 text-green-500" />
                </div>
                <h3 className="font-bold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-zinc-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-zinc-900/50" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-zinc-800 border border-zinc-700 text-zinc-300 px-4 py-2 rounded-md text-sm font-medium mb-6">
              <Star className="w-4 h-4 text-amber-500" fill="currentColor" />
              Carrier-First Feedback
            </div>
            <h2 className="text-4xl md:text-5xl font-black tracking-tighter mb-4">Built With Owner-Operators</h2>
            <p className="text-xl text-zinc-400">Early access feedback from truckers and brokers</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((testimonial, idx) => (
              <div key={idx} className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 hover:border-zinc-700 transition-colors">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-amber-500" fill="currentColor" />
                  ))}
                </div>
                <Quote className="w-8 h-8 text-blue-500/30 mb-3" />
                <p className="text-zinc-300 text-sm leading-relaxed mb-6">{testimonial.quote}</p>
                <div className="border-t border-zinc-800 pt-4">
                  <p className="font-semibold text-white">{testimonial.name}</p>
                  <p className="text-zinc-500 text-sm">{testimonial.role}</p>
                  <span className="inline-block mt-2 text-xs bg-zinc-800 text-zinc-400 px-2 py-1 rounded">
                    {testimonial.equipment}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20" data-testid="final-cta-section">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-black tracking-tighter mb-6">
            Ready to Move?
          </h2>
          <p className="text-xl text-zinc-400 mb-10">
            Reserve your spot — founding shippers and carriers get priority + locked-in pricing.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="/post-load" className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-10 py-5 rounded-md font-bold text-lg transition-colors shadow-[0_0_30px_rgba(59,130,246,0.4)]">
              <Package className="w-5 h-5" /> Post a Load
            </a>
            <a href="/loads" className="inline-flex items-center justify-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-white px-10 py-5 rounded-md font-bold text-lg transition-colors border border-zinc-700">
              <Truck className="w-5 h-5" /> Find Loads
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-zinc-900 border-t border-zinc-800 py-12" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img src={LOGO_URL} alt="Tempus Dispatch" className="h-16 mb-4" />
              <p className="text-zinc-500 text-sm">The freight marketplace where shippers and carriers connect.</p>
            </div>
            <div>
              <h4 className="font-bold text-white mb-4">For Shippers</h4>
              <ul className="space-y-2">
                <li><a href="/post-load" className="text-zinc-500 hover:text-white text-sm transition-colors">Post a Load</a></li>
                <li><a href="/admin" className="text-zinc-500 hover:text-white text-sm transition-colors">Shipper Login</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-white mb-4">For Carriers</h4>
              <ul className="space-y-2">
                <li><a href="/loads" className="text-zinc-500 hover:text-white text-sm transition-colors">Browse Loads</a></li>
                <li><a href="/driver-signup" className="text-zinc-500 hover:text-white text-sm transition-colors">Join Network</a></li>
                <li><a href="/alerts" className="text-zinc-500 hover:text-white text-sm transition-colors">Load Alerts</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-white mb-4">Company</h4>
              <ul className="space-y-2">
                <li><a href="mailto:givi@tempusdispatch.com" className="text-zinc-500 hover:text-white text-sm transition-colors">Contact Us</a></li>
                <li><a href="/terms" className="text-zinc-500 hover:text-white text-sm transition-colors">Terms of Service</a></li>
                <li><a href="/privacy" className="text-zinc-500 hover:text-white text-sm transition-colors">Privacy Policy</a></li>
                <li><a href="/admin" className="text-zinc-500 hover:text-white text-sm transition-colors">Admin Portal</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-zinc-800 pt-8 flex flex-col gap-3">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-zinc-600 text-sm">© 2026 Tempus Dispatch LLC. All rights reserved.</p>
              <p className="text-zinc-600 text-sm">SaaS + carrier dispatch agent · 6% carriers / flat tiers brokers &amp; shippers</p>
            </div>
            <p className="text-[11px] text-zinc-600 leading-relaxed">
              <strong className="text-zinc-500">Disclaimer:</strong> Tempus Dispatch LLC is a software (SaaS) platform — not a freight broker, motor carrier, or freight forwarder.
              We do not arrange transportation for compensation, hold an FMCSA Broker Authority, or process freight payments. All freight transactions
              occur directly between shippers/brokers and motor carriers.
            </p>
          </div>
        </div>
      </footer>

      {/* Floating AI Chat Widget */}
      <FloatingChatWidget />
    </div>
  );
};

export default LandingPage;
